import { useState, useEffect } from 'react'
import { Save, X, Tag, Eye, EyeOff } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'

export default function CollectionEditor({ collection, allTags, onSave, onCancel, setStatus }) {
  const { api } = useAuth()
  const isEditing = !!collection

  const [name, setName] = useState('')
  const [queryTags, setQueryTags] = useState([])
  const [queryType, setQueryType] = useState('OR')
  const [sortOrder, setSortOrder] = useState('most_recent')
  const [isActive, setIsActive] = useState(true)
  const [adsEnabled, setAdsEnabled] = useState(false)
  const [vastAdTag, setVastAdTag] = useState('')

  const [tagInput, setTagInput] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (collection) {
      setName(collection.name || '')
      setQueryTags(collection.query_tags ? collection.query_tags.split(',').map(t => t.trim()) : [])
      setQueryType(collection.query_type || 'OR')
      setSortOrder(collection.sort_order || 'most_recent')
      setIsActive(collection.is_active === 1 || collection.is_active === true)
      setAdsEnabled(collection.ads_enabled === 1 || collection.ads_enabled === true)
      setVastAdTag(collection.vast_ad_tag || '')
    } else {
      setName('')
      setQueryTags([])
      setQueryType('OR')
      setSortOrder('most_recent')
      setIsActive(true)
      setAdsEnabled(false)
      setVastAdTag('')
    }
  }, [collection])

  async function handleSubmit(e) {
    e.preventDefault()

    if (!name.trim()) {
      setStatus('Error: Name is required')
      return
    }
    if (queryTags.length === 0) {
      setStatus('Error: At least one tag is required')
      return
    }

    setSaving(true)
    try {
      const payload = {
        name: name.trim(),
        query_tags: queryTags.join(','),
        query_type: queryType,
        sort_order: sortOrder,
        is_active: isActive ? 1 : 0,
        ads_enabled: adsEnabled ? 1 : 0,
        vast_ad_tag: adsEnabled ? vastAdTag.trim() || null : null,
      }

      if (isEditing) {
        await api(`/vod/collections/${collection.id}`, {
          method: 'PATCH',
          body: JSON.stringify(payload)
        })
        setStatus('✅ Collection updated')
      } else {
        await api('/vod/collections', {
          method: 'POST',
          body: JSON.stringify(payload)
        })
        setStatus('✅ Collection created')
      }
      onSave()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setSaving(false)
  }

  function addTag(tagName) {
    const clean = tagName.trim().toLowerCase()
    if (!clean || queryTags.includes(clean)) return
    setQueryTags(prev => [...prev, clean])
    setTagInput('')
    setShowSuggestions(false)
  }

  function removeTag(tagName) {
    setQueryTags(prev => prev.filter(t => t !== tagName))
  }

  function handleTagKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault()
      addTag(tagInput)
    }
    if (e.key === 'Backspace' && !tagInput && queryTags.length > 0) {
      removeTag(queryTags[queryTags.length - 1])
    }
  }

  // Combine allTags with any new tags the user has added locally
  const combinedTags = [...new Set([...allTags, ...queryTags])]
  
  const filteredSuggestions = tagInput.length > 0
    ? combinedTags.filter(t => t.includes(tagInput.toLowerCase()) && !queryTags.includes(t))
    : []

  return (
    <div className="collection-editor">
      <div className="editor-header">
        <h2>{isEditing ? 'Edit Collection' : 'New Collection'}</h2>
        <button className="btn-ghost" onClick={onCancel}>
          <X size={16} />
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="editor-section">
          <div className="form-row">
            <label>Collection Name *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g., PIHL 2025/26 Season"
              required
            />
          </div>

          <div className="form-row">
            <label><Tag size={14} /> Filter Tags *</label>
            <p className="form-hint">
              Videos with these tags will appear in this collection
            </p>
            <div className="tags-input-wrap">
              <div className="tags-chip-list">
                {queryTags.map(tag => (
                  <span key={tag} className="tag-chip">
                    {tag}
                    <button type="button" className="tag-remove" onClick={() => removeTag(tag)}>
                      <X size={12} />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  className="tag-text-input"
                  value={tagInput}
                  onChange={e => {
                    setTagInput(e.target.value)
                    setShowSuggestions(true)
                  }}
                  onKeyDown={handleTagKeyDown}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  placeholder={queryTags.length === 0 ? 'Add tags...' : ''}
                />
              </div>
              {showSuggestions && filteredSuggestions.length > 0 && (
                <div className="tag-suggestions">
                  {filteredSuggestions.map(s => (
                    <div key={s} className="tag-suggestion" onMouseDown={() => addTag(s)}>
                      {s}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="form-row">
            <label>Query Type</label>
            <p className="form-hint">
              How to match videos with multiple tags
            </p>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="queryType"
                  value="OR"
                  checked={queryType === 'OR'}
                  onChange={e => setQueryType(e.target.value)}
                />
                <span className="radio-text">
                  <strong>OR</strong> - Videos with ANY of these tags
                </span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="queryType"
                  value="AND"
                  checked={queryType === 'AND'}
                  onChange={e => setQueryType(e.target.value)}
                />
                <span className="radio-text">
                  <strong>AND</strong> - Videos with ALL of these tags
                </span>
              </label>
            </div>
          </div>

          <div className="form-row">
            <label>Sort Order</label>
            <select value={sortOrder} onChange={e => setSortOrder(e.target.value)}>
              <option value="most_recent">Most Recent First</option>
              <option value="oldest">Oldest First</option>
              <option value="alphabetical">Alphabetical (A-Z)</option>
            </select>
          </div>

          <div className="form-row">
            <label>Visibility</label>
            <div className="toggle-row">
              <button
                type="button"
                className={`visibility-toggle ${isActive ? 'active' : 'inactive'}`}
                onClick={() => setIsActive(!isActive)}
              >
                {isActive ? <Eye size={16} /> : <EyeOff size={16} />}
                {isActive ? 'Active (Visible in Apps)' : 'Hidden (Draft)'}
              </button>
            </div>
          </div>

          <div className="form-row">
            <label>Pre-Roll Ads</label>
            <div className="toggle-row">
              <button
                type="button"
                className={`visibility-toggle ${adsEnabled ? 'active' : 'inactive'}`}
                onClick={() => setAdsEnabled(!adsEnabled)}
              >
                {adsEnabled ? '📺 Pre-Roll Ads Enabled' : 'No Pre-Roll Ads'}
              </button>
            </div>
            {adsEnabled && (
              <div style={{ marginTop: 8 }}>
                <label style={{ fontSize: 12, marginBottom: 4, display: 'block' }}>VAST Ad Tag URL</label>
                <input
                  type="text"
                  value={vastAdTag}
                  onChange={e => setVastAdTag(e.target.value)}
                  placeholder="https://ads.tenband.tv/ad.php?do=vast&zid=..."
                  style={{ width: '100%' }}
                />
                <p className="form-hint" style={{ marginTop: 4 }}>
                  VAST 3.0 tag from your ad server. Pre-roll will play before videos selected from this collection.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="editor-actions">
          <button type="button" className="btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            <Save size={14} /> {saving ? 'Saving...' : (isEditing ? 'Update Collection' : 'Create Collection')}
          </button>
        </div>
      </form>
    </div>
  )
}
